/*
 * Dry / processed listening for plugin-audio.liquid.
 * Both files of an example play together from one AudioContext clock, so the
 * Dry / Venin switch is a short gain crossfade: instantaneous, sample-aligned
 * and click-free. Files are fetched when the component nears the viewport and
 * decoded on the first press (browsers require a gesture to start audio).
 */
(() => {
  if (customElements.get('plugin-audio')) return;

  const FADE = 0.012; // seconds (time constant of the switch crossfade)

  /** Peak envelope for the small waveform, in `count` columns. */
  function envelope(buffer, count) {
    const channels = [...Array(buffer.numberOfChannels).keys()].map((c) => buffer.getChannelData(c));
    const size = Math.floor(buffer.length / count);
    const out = new Float32Array(count);
    let max = 0;
    for (let i = 0; i < count; i += 1) {
      let peak = 0;
      const start = i * size;
      for (let j = start; j < start + size; j += 8) {
        for (const data of channels) peak = Math.max(peak, Math.abs(data[j]));
      }
      out[i] = peak;
      max = Math.max(max, peak);
    }
    return { values: out, max };
  }

  class PluginAudio extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.items = [...this.querySelectorAll('[data-audio-item]')].map((button) => ({
        button,
        urls: { dry: button.dataset.dry, wet: button.dataset.wet },
        raw: null,
        buffers: null,
        shapes: null,
      }));
      this.playButton = this.querySelector('[data-audio-play]');
      this.playLabel = this.querySelector('[data-audio-play-label]');
      this.versions = [...this.querySelectorAll('[data-audio-version]')];
      this.status = this.querySelector('[data-audio-status]');
      this.canvas = this.querySelector('[data-audio-wave]');
      this.active = 0;
      this.version = 'dry';
      this.playing = false;
      this.offset = 0;
      if (!this.items.length || !(window.AudioContext || window.webkitAudioContext)) {
        this.playButton.disabled = true;
        this.announce('Audio playback is not available in this browser.');
        return;
      }

      this.playButton.addEventListener('click', () => (this.playing ? this.pause() : this.play()), { signal });
      this.versions.forEach((button) => {
        button.addEventListener('click', () => this.setVersion(button.dataset.audioVersion), { signal });
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          const next = this.version === 'dry' ? 'wet' : 'dry';
          this.setVersion(next);
          this.versions.find((b) => b.dataset.audioVersion === next)?.focus();
        }, { signal });
      });
      this.items.forEach((item, index) => {
        item.button.addEventListener('click', () => this.select(index, true), { signal });
      });
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.decode(0).catch(() => {});
        }
      }, { rootMargin: '600px 0px' });
      this.observer.observe(this);
      window.addEventListener('resize', () => this.draw(), { signal });
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.stopSources();
      cancelAnimationFrame(this.frame);
      this.context?.close();
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    prefetch(index) {
      const item = this.items[index];
      if (!item || item.raw) return item?.raw;
      item.raw = Promise.all(['dry', 'wet'].map((kind) => fetch(item.urls[kind]).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.arrayBuffer();
      })));
      item.raw.catch(() => { item.raw = null; });
      return item.raw;
    }

    ensureContext() {
      if (!this.context) {
        const Context = window.AudioContext || window.webkitAudioContext;
        // iOS: play through the ringer switch like media, not like UI sounds.
        if (navigator.audioSession) {
          try { navigator.audioSession.type = 'playback'; } catch { /* unsupported */ }
        }
        this.context = new Context({ latencyHint: 'playback' });
        this.master = this.context.createGain();
        this.master.connect(this.context.destination);
      }
      return this.context;
    }

    /**
     * Decodes both signals once. Before the first press this uses an OfflineAudioContext
     * (no gesture needed) so the envelope can be drawn; AudioBuffers play in any context.
     */
    decode(index) {
      const item = this.items[index];
      if (item.buffers) return Promise.resolve(item.buffers);
      if (item.decoding) return item.decoding;
      const Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext;
      const context = this.context ?? (Offline ? new Offline(2, 1, 44100) : this.ensureContext());
      item.decoding = this.prefetch(index)
        // decodeAudioData detaches its input, so decode copies.
        .then(([dry, wet]) => Promise.all([dry, wet].map((data) => context.decodeAudioData(data.slice(0)))))
        .then((buffers) => {
          item.buffers = { dry: buffers[0], wet: buffers[1] };
          item.shapes = { dry: envelope(buffers[0], 240), wet: envelope(buffers[1], 240) };
          if (index === this.active) this.draw();
          return item.buffers;
        })
        .catch((error) => { item.decoding = null; throw error; });
      return item.decoding;
    }

    async play() {
      const index = this.active;
      const token = (this.token = Symbol('play'));
      this.ensureContext();
      this.playing = true;
      this.render();
      try {
        if (this.context.state !== 'running') await this.context.resume();
        if (!this.items[index].buffers) this.announce('Loading audio…');
        const buffers = await this.decode(index);
        if (token !== this.token || !this.isConnected) return;
        this.stopSources();
        const when = this.context.currentTime + 0.03;
        const duration = buffers.dry.duration;
        const offset = this.offset % duration;
        this.sources = {};
        this.gains = {};
        for (const kind of ['dry', 'wet']) {
          const source = this.context.createBufferSource();
          source.buffer = buffers[kind];
          source.loop = true;
          const gain = this.context.createGain();
          gain.gain.value = kind === this.version ? 1 : 0;
          source.connect(gain).connect(this.master);
          source.start(when, offset);
          this.sources[kind] = source;
          this.gains[kind] = gain;
        }
        this.startedAt = when - offset;
        this.duration = duration;
        this.announce(this.describe());
        this.draw();
        this.tick();
        this.prefetch(index + 1);
      } catch (error) {
        if (token !== this.token) return;
        this.playing = false;
        this.render();
        this.announce('This example could not be loaded. Please try again.');
      }
    }

    pause() {
      this.token = null;
      if (this.context && this.startedAt !== undefined && this.duration) {
        this.offset = (this.context.currentTime - this.startedAt) % this.duration;
      }
      this.stopSources();
      this.playing = false;
      cancelAnimationFrame(this.frame);
      this.render();
      this.announce('Paused.');
    }

    stopSources() {
      if (!this.sources) return;
      for (const source of Object.values(this.sources)) {
        try { source.stop(); } catch { /* already stopped */ }
        source.disconnect();
      }
      this.sources = null;
      this.gains = null;
    }

    setVersion(version) {
      if (!['dry', 'wet'].includes(version) || version === this.version) return;
      this.version = version;
      if (this.gains && this.context) {
        const now = this.context.currentTime;
        for (const [kind, gain] of Object.entries(this.gains)) {
          gain.gain.cancelScheduledValues(now);
          gain.gain.setTargetAtTime(kind === version ? 1 : 0, now, FADE);
        }
      }
      this.render();
      this.draw();
      if (this.playing) this.announce(this.describe());
    }

    select(index, startPlaying) {
      if (index === this.active) {
        if (startPlaying && !this.playing) this.play();
        return;
      }
      const wasPlaying = this.playing;
      this.token = null;
      this.stopSources();
      cancelAnimationFrame(this.frame);
      this.active = index;
      this.offset = 0;
      this.startedAt = undefined;
      this.playing = false;
      this.render();
      this.draw();
      if (startPlaying || wasPlaying) this.play();
      else this.decode(index).catch(() => {});
    }

    describe() {
      const item = this.items[this.active].button;
      const name = item.querySelector('.plugin-audio__result')?.textContent ?? '';
      return `${name}: ${this.version === 'dry' ? 'dry source' : 'processed through Venin'}.`;
    }

    position() {
      if (!this.playing || !this.context || this.startedAt === undefined) return this.offset;
      return (this.context.currentTime - this.startedAt) % this.duration;
    }

    tick() {
      cancelAnimationFrame(this.frame);
      const step = () => {
        if (!this.playing) return;
        const progress = this.duration ? this.position() / this.duration : 0;
        this.items[this.active].button.style.setProperty('--progress', progress.toFixed(4));
        this.style.setProperty('--playhead', progress.toFixed(4));
        this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    }

    draw() {
      const canvas = this.canvas;
      const shapes = this.items[this.active]?.shapes;
      if (!canvas) return;
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      const width = Math.round(canvas.clientWidth * ratio);
      const height = Math.round(canvas.clientHeight * ratio);
      if (!width || !height) return;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      const g = canvas.getContext('2d');
      g.clearRect(0, 0, width, height);
      if (!shapes) return;
      const max = Math.max(shapes.dry.max, shapes.wet.max) || 1;
      const style = getComputedStyle(this);
      const colours = {
        dry: style.getPropertyValue('--audio-dry').trim() || '#d8d6d0',
        wet: style.getPropertyValue('--audio-wet').trim() || '#ef6268',
      };
      const order = this.version === 'dry' ? ['wet', 'dry'] : ['dry', 'wet'];
      for (const kind of order) {
        const values = shapes[kind].values;
        const column = width / values.length;
        g.fillStyle = colours[kind];
        g.globalAlpha = kind === this.version ? 0.95 : 0.2;
        for (let i = 0; i < values.length; i += 1) {
          const h = Math.max(ratio, (values[i] / max) * height * 0.92);
          g.fillRect(i * column + column * 0.2, (height - h) / 2, Math.max(1, column * 0.55), h);
        }
      }
      g.globalAlpha = 1;
    }

    render() {
      this.classList.toggle('is-playing', this.playing);
      this.dataset.version = this.version;
      this.playLabel.textContent = this.playing ? 'Pause' : 'Play';
      this.playButton.setAttribute('aria-label', this.playing ? 'Pause' : 'Play');
      this.versions.forEach((button) => {
        button.setAttribute('aria-checked', String(button.dataset.audioVersion === this.version));
        button.tabIndex = button.dataset.audioVersion === this.version ? 0 : -1;
      });
      this.items.forEach((item, index) => {
        item.button.setAttribute('aria-pressed', String(index === this.active));
        if (index !== this.active) item.button.style.removeProperty('--progress');
      });
    }
  }

  customElements.define('plugin-audio', PluginAudio);
})();
